class PreOrderForm {
  constructor() {
    this.forms = document.querySelectorAll('[id^="pre-order-form-container-"][id$="__main"]');
    this.variantChangeUnsubscriber = undefined;
    this.currentVariantId = null;
    this.currentProductData = null;
    this.initialized = false;
    this.init();
  }

  init() {
    if (this.initialized) {
      return;
    }

    this.forms.forEach((form) => {
      this.setupForm(form);
      this.ensureInitialButtonState(form);
    });

    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );

    this.updateCurrentVariantData();

    this.initialized = true;
  }

  ensureInitialButtonState(form) {
    const buttonText = form.querySelector('.pre-order-form__button-text');
    const buttonLoading = form.querySelector('.pre-order-form__button-loading');
    const submitButton = form.querySelector('button[type="button"]');
    const statusElement = form.querySelector('[role="status"]');
    const errorElement = form.querySelector('[role="alert"]');

    if (buttonText && buttonLoading && submitButton) {
      buttonText.hidden = false;
      buttonText.style.display = '';
      buttonText.style.visibility = '';

      buttonLoading.hidden = true;
      buttonLoading.style.display = 'none';
      buttonLoading.style.visibility = 'hidden';

      submitButton.disabled = false;
    }

    if (statusElement) {
      statusElement.hidden = true;
      statusElement.textContent = '';
      statusElement.className = 'pre-order-form__status';
    }

    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  handleVariantChange(event) {
    const { data } = event;
    if (data && data.variant) {
      this.currentVariantId = data.variant.id;
      this.currentProductData = {
        productId: data.variant.product_id,
        productTitle: document.querySelector('h1')?.textContent?.trim() || 'Product',
        variantId: data.variant.id
      };

      this.updateFormVariantInfo();
    }
  }

  updateFormVariantInfo() {}

  updateCurrentVariantData() {
    const variantInput = document.querySelector('input[name="id"]');
    const variantSelect = document.querySelector('select[name="id"]');

    if (variantInput && variantInput.value) {
      this.currentVariantId = parseInt(variantInput.value, 10);
    } else if (variantSelect && variantSelect.value) {
      this.currentVariantId = parseInt(variantSelect.value, 10);
    }

    this.currentProductData = this.getProductData();
  }

  setupForm(form) {
    const submitButton = form.querySelector('button[type="button"]');
    const emailInput = form.querySelector('input[type="email"]');
    const errorElement = form.querySelector('[role="alert"]');
    const statusElement = form.querySelector('[role="status"]');
    const buttonText = form.querySelector('.pre-order-form__button-text');
    const buttonLoading = form.querySelector('.pre-order-form__button-loading');

    if (!submitButton || !emailInput) {
      return;
    }

    if (buttonText) buttonText.hidden = false;
    if (buttonLoading) buttonLoading.hidden = true;
    if (statusElement) statusElement.hidden = true;
    if (errorElement) errorElement.hidden = true;

    submitButton.addEventListener('click', async (e) => {
      e.preventDefault();

      const email = emailInput.value.trim();

      if (!this.validateEmail(email)) {
        this.showError(errorElement, this.getTranslation('products.product.pre_order.error'));
        return;
      }

      this.setLoadingState(submitButton, buttonText, buttonLoading, true);
      this.hideError(errorElement);
      this.hideStatus(statusElement);

      try {
        await this.submitToKlaviyo(email, form);
        this.showSuccess(statusElement, this.getTranslation('products.product.pre_order.success'));
        emailInput.value = '';
      } catch (error) {
        console.error('Pre-order submission error:', error);
        this.showError(errorElement, 'Something went wrong. Please try again.');
      } finally {
        this.setLoadingState(submitButton, buttonText, buttonLoading, false);
      }
    });

    emailInput.addEventListener('input', () => {
      this.hideError(errorElement);
      this.hideStatus(statusElement);
    });
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  async submitToKlaviyo(email, form) {
    const productData = this.currentProductData || this.getProductData();

    if (!productData.variantId) {
      throw new Error('Product variant ID not found');
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email
              }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${productData.variantId}`
            }
          }
        }
      }
    };

    try {
      const response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=SxKywd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2025-07-15'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;

        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (_) {
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = `${errorMessage} - ${errorText}`;
            }
          } catch (_) {}
        }

        throw new Error(errorMessage);
      }

      if (response.status === 202 || response.headers.get('content-length') === '0') {
        return { success: true, status: response.status };
      }

      return await response.json();
    } catch (error) {
      console.error('Klaviyo request failed:', error);
      throw error;
    }
  }

  getProductData() {
    const productData = window.Shopify?.analytics?.meta?.product;

    if (productData) {
      return {
        productId: productData.id,
        productTitle: productData.title,
        variantId: productData.selected_or_first_available_variant?.id
      };
    }

    const metaProduct = document.querySelector('meta[property="product:retailer_item_id"]');
    const metaVariant = document.querySelector('meta[property="product:price:amount"]');

    if (metaProduct && metaVariant) {
      return {
        productId: metaProduct.content,
        productTitle: document.querySelector('h1')?.textContent?.trim() || 'Product',
        variantId: this.extractVariantIdFromPage()
      };
    }

    return {
      productId: null,
      productTitle: document.querySelector('h1')?.textContent?.trim() || 'Product',
      variantId: this.extractVariantIdFromPage()
    };
  }

  extractVariantIdFromPage() {
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      return parseInt(variantInput.value, 10);
    }

    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect) {
      return parseInt(variantSelect.value, 10);
    }

    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const hiddenInput = productForm.querySelector('input[name="id"]');
      if (hiddenInput) {
        return parseInt(hiddenInput.value, 10);
      }
    }

    return null;
  }

  setLoadingState(button, buttonText, buttonLoading, isLoading) {
    if (!button || !buttonText || !buttonLoading) {
      return;
    }

    if (isLoading) {
      button.disabled = true;
      buttonText.hidden = true;
      buttonLoading.hidden = false;
    } else {
      button.disabled = false;
      buttonText.hidden = false;
      buttonLoading.hidden = true;
    }
  }

  showError(errorElement, message) {
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.hidden = false;
    }
  }

  hideError(errorElement) {
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  showSuccess(statusElement, message) {
    if (!statusElement) {
      return;
    }

    const form = statusElement.closest('.pre-order-form__form');
    if (form) {
      const emailField = form.querySelector('.pre-order-form__field');
      const submitButton = form.querySelector('button[type="button"]');

      if (emailField) emailField.classList.add('hidden');
      if (submitButton) submitButton.classList.add('hidden');
    }

    statusElement.textContent = message;
    statusElement.hidden = false;
    statusElement.classList.remove('hidden');
    statusElement.classList.add('pre-order-form__status--success');
  }

  hideStatus(statusElement) {
    if (statusElement) {
      statusElement.hidden = true;
      statusElement.textContent = '';
      statusElement.className = 'pre-order-form__status';
    }
  }

  getTranslation(key) {
    const translations = {
      'products.product.pre_order.error': 'Please enter a valid email address.',
      'products.product.pre_order.success': 'Thank you! We\'ll notify you when this item is back in stock.'
    };
    return translations[key] || key;
  }

  destroy() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }
}

let preOrderFormInstance = null;
let domContentLoadedFired = false;

document.addEventListener('DOMContentLoaded', () => {
  if (domContentLoadedFired) {
    return;
  }

  domContentLoadedFired = true;

  const preOrderElements = document.querySelectorAll('[id^="pre-order-form-container-"]');

  if (preOrderElements.length > 0) {
    setTimeout(() => {
      if (!preOrderFormInstance) {
        preOrderFormInstance = new PreOrderForm();
      }
    }, 100);
  }
});

window.addEventListener('beforeunload', () => {
  if (preOrderFormInstance) {
    preOrderFormInstance.destroy();
  }
});